import { call, put, select } from 'redux-saga/effects';
import {
  exceptionsActionFailure,
  exceptionsActionSuccess,
  fetchActivityLogsRequest,
  loadReportRequest,
  loadReportTotalsRequest,
} from '../actions';
import { ApiError } from 'store/api/types';
import { ExceptionsActionRequestAction } from '../types';
import { checkResponse } from 'store/utils';

import { exceptionsAction } from '../api';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { getVatRunData } from '../selectors';
import { VatLoadRequestData } from '../models/requestData';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import { DataLogStatus } from 'store/app/enums';
import i18n from 'i18next';
import { AllExceptionsDetails } from '../models/exceptionWrapper';

function hasDistinctOverrides(
  allExceptionsDetails: AllExceptionsDetails | null | undefined,
  exceptionsWrapper: { exceptionActionType: string }[]
) {
  const bulkAction = allExceptionsDetails?.actionType;
  if (!bulkAction || !exceptionsWrapper?.length) {
    return false;
  }
  return exceptionsWrapper.some((ew) => ew.exceptionActionType !== bulkAction);
}

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* saveExceptionsActionSaga(
  action: ExceptionsActionRequestAction
) {
  try {
    let err: ApiError | null;
    const {
      dataLogId,
      mappingId,
      exceptionsWrapper,
      reportId,
      allExceptionsDetails,
      oldReportId,
    } = action.payload;

    const applyOverridesSeparately = hasDistinctOverrides(
      allExceptionsDetails,
      exceptionsWrapper
    );

    // Select-all uses allExceptionsDetails. A later change to one row is stored
    // in exceptionsActionList. Sending both in one request lets the bulk action
    // win, so apply bulk first, then the per-row overrides (same as a direct action).
    const bulkResponse = yield call(
      exceptionsAction,
      dataLogId,
      mappingId,
      applyOverridesSeparately ? [] : exceptionsWrapper,
      allExceptionsDetails,
      oldReportId
    );

    err = checkResponse(bulkResponse?.responseMessage);
    if (err) throw err;

    let response = bulkResponse;

    if (applyOverridesSeparately) {
      response = yield call(
        exceptionsAction,
        dataLogId,
        mappingId,
        exceptionsWrapper,
        null,
        oldReportId
      );
      err = checkResponse(response?.responseMessage);
      if (err) throw err;
    }

    yield put(exceptionsActionSuccess());
    const vatRunData = yield select(getVatRunData);
    const vatLoadRequestData: VatLoadRequestData = {
      connectionId: vatRunData.tokenId,
      fromDate: vatRunData.fromDate,
      toDate: vatRunData.toDate,
      currency: vatRunData.currency,
      mappingId,
      dataLogId,
      provider: vatRunData.providerName?.toLowerCase(),
    };

    // Check if status is SUBMITTED - if so, use loadReportTotals instead of loadReport
    if (
      vatRunData.status &&
      DataLogStatus[vatRunData.status] === DataLogStatus.SUBMITTED
    ) {
      yield put(loadReportTotalsRequest(mappingId, dataLogId));
    } else {
      yield put(loadReportRequest(vatLoadRequestData, reportId));
    }

    yield put(fetchActivityLogsRequest(dataLogId));

    const message: MessageData = {
      title: i18n.t('common.success-exclamation'),
      description: response.responseMessage.message,
      type: MessageStates.SUCCESS,
    };

    yield put(turnMessageOn(message));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(exceptionsActionFailure());
  }
}
